import {
    type PointerEvent,
    useLayoutEffect,
    type MouseEvent,
    type ReactNode,
    useCallback,
    useState,
    useMemo,
    useRef,
    useId
} from "react";
import {
    View
} from "react-native";
import {
    useAnatomyStyle
} from "./style";
import {
    type AnatomyPointer,
    AnatomyChainView,
    AnatomyBalloons,
    AnatomyRulers,
    AnatomyObject,
    AnatomyFocus,
    AnatomyHatch,
    AnatomyGrid
} from "./drawing";
import {
    createAnatomyLayout,
    getScaleBarLength,
    type AnatomyGhost,
    formatValue
} from "./layout";
import {
    AnatomyLegend
} from "./legend";
import {
    type AnatomyMeasurement,
    isSameMeasurement,
    type AnatomyRect,
    type AnatomyPart,
    measureAnatomy
} from "./measure";
import {
    findSizeTokens
} from "./tokens";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsThemeName
} from "../../theme";
import {
    ToggleGroup,
    Card,
    Text
} from "ncore-ui-kit";
import {
    DemoFrame,
    Caption
} from "../content";

export type AnatomyStateOption = {
    node: ReactNode;
    label: string;
};

const DEFAULT_MAX_SCALE = 5;

const DEFAULT_MAX_HEIGHT = 300;

const MAX_LISTED_PARTS = 12;

const HIT_COVERAGE_LIMIT = 0.9;

const SCALE_BAR_HEIGHT = 30;

const SUBJECT_STYLE = {
    position: "absolute",
    left: 0,
    top: 0
} as const;

const SCROLL_STYLE = {
    overscrollBehaviorX: "contain",
    overflowX: "auto"
} as const;

const STAGE_STYLE = {
    position: "relative",
    overflow: "hidden"
} as const;

const SUBJECTS_STYLE = {
    pointerEvents: "none",
    position: "absolute",
    opacity: 0,
    left: 0,
    top: 0
} as const;

const OVERLAY_STYLE = {
    pointerEvents: "none",
    position: "absolute",
    overflow: "visible",
    left: 0,
    top: 0
} as const;

const FOOTER_STYLE = {
    justifyContent: "space-between",
    boxSizing: "border-box",
    alignItems: "flex-end",
    position: "absolute",
    flexWrap: "wrap",
    display: "flex",
    bottom: 0,
    left: 0
} as const;

const FOOTER_START_STYLE = {
    flexDirection: "column",
    display: "flex",
    minWidth: 0
} as const;

const readThemeName = (): DocsThemeName => document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";

const getListedParts = (parts: Array<AnatomyPart>) => [...parts].sort((first, second) => first.depth - second.depth || second.rect.width * second.rect.height - first.rect.width * first.rect.height).slice(0, MAX_LISTED_PARTS).sort((first, second) => first.id - second.id);

const getCoverage = (measurement: AnatomyMeasurement) => {
    const hit = measurement.hitRect;

    if(!hit || hit.width * hit.height === 0) {
        return 1;
    }

    const covered = measurement.parts.filter((part) => part.parentId === null).reduce((total, part) => {
        const width = Math.max(0, Math.min(part.rect.x + part.rect.width, hit.x + hit.width) - Math.max(part.rect.x, hit.x));

        const height = Math.max(0, Math.min(part.rect.y + part.rect.height, hit.y + hit.height) - Math.max(part.rect.y, hit.y));

        return total + width * height;
    }, 0);

    return covered / (hit.width * hit.height);
};

const getSignature = (part: AnatomyPart) => [
    part.kind,
    Math.round(part.rect.width),
    Math.round(part.rect.height),
    Math.round(part.radius),
    part.background ?? "",
    part.kind === "text" ? part.text ?? "" : ""
].join("|");

const getLooseSignature = (part: AnatomyPart) => [
    part.kind,
    Math.round(part.rect.height),
    Math.round(part.radius),
    part.background ?? ""
].join("|");

const isSamePosition = (first: AnatomyRect, second: AnatomyRect) => Math.abs(first.x - second.x) < 0.5 && Math.abs(first.y - second.y) < 0.5;

const getDistance = (first: AnatomyRect, second: AnatomyRect) => Math.hypot(first.x - second.x, first.y - second.y);

const getGhosts = (measurement: AnatomyMeasurement, stateMeasurements: Array<AnatomyMeasurement | null>, activeState: number) => {
    const ghosts: Array<AnatomyGhost> = [];

    stateMeasurements.forEach((other, stateIndex) => {
        if(!other || stateIndex === activeState) {
            return;
        }

        const unmatchedIds = new Set(other.parts.map((part) => part.id));

        const movedParts = measurement.parts.filter((part) => {
            const signature = getSignature(part);

            const stayed = other.parts.find((candidate) => unmatchedIds.has(candidate.id) && getSignature(candidate) === signature && isSamePosition(candidate.rect, part.rect));

            if(stayed) {
                unmatchedIds.delete(stayed.id);

                return false;
            }

            return true;
        });

        movedParts.forEach((part) => {
            const signature = getSignature(part);

            const looseSignature = getLooseSignature(part);

            const candidates = other.parts.filter((candidate) => unmatchedIds.has(candidate.id) && getSignature(candidate) === signature);

            const looseCandidates = part.kind === "shape" ? other.parts.filter((candidate) => unmatchedIds.has(candidate.id) && getLooseSignature(candidate) === looseSignature) : [];

            const [
                nearest
            ] = (candidates.length > 0 ? candidates : looseCandidates).sort((first, second) => getDistance(first.rect, part.rect) - getDistance(second.rect, part.rect));

            if(!nearest) {
                return;
            }

            unmatchedIds.delete(nearest.id);

            const isParentMoved = ghosts.some((ghost) => ghost.stateIndex === stateIndex && ghost.partId === part.parentId);

            if(!isParentMoved) {
                ghosts.push({
                    rect: nearest.rect,
                    partId: part.id,
                    stateIndex
                });
            }
        });
    });

    return ghosts;
};

const Anatomy = ({
    maxHeight = DEFAULT_MAX_HEIGHT,
    maxScale = DEFAULT_MAX_SCALE,
    children,
    caption,
    states,
    names,
    notes,
    width,
    name
}: {
    states?: Array<AnatomyStateOption>;
    names?: Array<string>;
    notes?: Array<string>;
    children?: ReactNode;
    maxHeight?: number;
    maxScale?: number;
    caption?: string;
    width?: number;
    name: string;
}) => {
    const {
        language,
        t
    } = useDocsI18n();

    const id = useId().replace(/:/g, "");

    const style = useAnatomyStyle();

    const frameRef = useRef<HTMLDivElement>(null);

    const stageRef = useRef<HTMLDivElement>(null);

    const subjectsRef = useRef<HTMLDivElement>(null);

    const activeSubjectRef = useRef<HTMLDivElement>(null);

    const stateSubjectRefs = useRef<Array<HTMLDivElement | null>>([]);

    const frameRequestRef = useRef<number | null>(null);

    const footerRef = useRef<HTMLDivElement>(null);

    const stateOptions: Array<AnatomyStateOption> = states && states.length > 0 ? states : [
        {
            node: children,
            label: ""
        }
    ];

    const hasStates = stateOptions.length > 1;

    const [
        activeState,
        setActiveState
    ] = useState(0);

    const [
        footerHeight,
        setFooterHeight
    ] = useState(0);

    const [
        availableWidth,
        setAvailableWidth
    ] = useState(0);

    const [
        measurement,
        setMeasurement
    ] = useState<AnatomyMeasurement | null>(null);

    const [
        stateMeasurements,
        setStateMeasurements
    ] = useState<Array<AnatomyMeasurement | null>>([]);

    const [
        pointer,
        setPointer
    ] = useState<AnatomyPointer | null>(null);

    const [
        hoveredPartId,
        setHoveredPartId
    ] = useState<number | null>(null);

    const [
        pinnedPartId,
        setPinnedPartId
    ] = useState<number | null>(null);

    const [
        activeSegmentKey,
        setActiveSegmentKey
    ] = useState<string | null>(null);

    const [
        activeCallout,
        setActiveCallout
    ] = useState<number | null | undefined>(undefined);

    const locale = language === "tr" ? "tr-TR" : "en-US";

    const upper = useCallback((value: string) => value.toLocaleUpperCase(locale), [
        locale
    ]);

    const listedParts = useMemo(() => measurement ? getListedParts(measurement.parts) : [], [
        measurement
    ]);

    const ghosts = useMemo(() => measurement && hasStates ? getGhosts(measurement, stateMeasurements, activeState) : [], [
        stateMeasurements,
        measurement,
        activeState,
        hasStates
    ]);

    const isHitAreaVisible = measurement ? getCoverage(measurement) < HIT_COVERAGE_LIMIT : false;

    const hitArea = useMemo(() => {
        const hit = measurement?.hitRect;

        if(!hit || !isHitAreaVisible) {
            return null;
        }

        return {
            token: [
                ...findSizeTokens("size", hit.height),
                ...findSizeTokens("size", hit.width)
            ].find((name) => name.startsWith("controlHeights.") || name.startsWith("touchTargets.")) ?? null,
            number: listedParts.length + 1,
            height: hit.height,
            width: hit.width
        };
    }, [
        isHitAreaVisible,
        listedParts,
        measurement
    ]);

    const balloonTargets = useMemo(() => [
        ...listedParts.map((part, index) => ({
            number: index + 1,
            partId: part.id
        })),
        ...hitArea ? [
            {
                number: hitArea.number,
                partId: null
            }
        ] : []
    ], [
        listedParts,
        hitArea
    ]);

    const layout = useMemo(() => {
        if(!measurement || availableWidth === 0) {
            return null;
        }

        return createAnatomyLayout({
            travelPrefix: upper(t("anatomy.travel")),
            availableWidth,
            balloonTargets,
            measurement,
            maxHeight,
            maxScale,
            ghosts
        });
    }, [
        availableWidth,
        balloonTargets,
        measurement,
        maxHeight,
        maxScale,
        ghosts,
        upper,
        t
    ]);

    const scheduleMeasure = useCallback(() => {
        if(frameRequestRef.current !== null) {
            return;
        }

        frameRequestRef.current = window.requestAnimationFrame(() => {
            frameRequestRef.current = null;

            const themeName = readThemeName();

            const subject = activeSubjectRef.current;

            if(subject) {
                const nextMeasurement = measureAnatomy(subject, themeName);

                setMeasurement((current) => isSameMeasurement(current, nextMeasurement) ? current : nextMeasurement);
            }

            const nextStateMeasurements = stateSubjectRefs.current.map((stateSubject) => stateSubject ? measureAnatomy(stateSubject, themeName) : null);

            setStateMeasurements((current) => JSON.stringify(current) === JSON.stringify(nextStateMeasurements) ? current : nextStateMeasurements);
        });
    }, []);

    useLayoutEffect(() => {
        const subjects = subjectsRef.current;

        const frame = frameRef.current;

        if(!subjects || !frame) {
            return;
        }

        scheduleMeasure();

        const frameObserver = new ResizeObserver((entries) => {
            setAvailableWidth(Math.floor(entries[0]?.contentRect.width ?? 0));
        });

        const subjectObserver = new ResizeObserver(scheduleMeasure);

        const mutationObserver = new MutationObserver(scheduleMeasure);

        const themeObserver = new MutationObserver(scheduleMeasure);

        frameObserver.observe(frame);

        Array.from(subjects.children).forEach((subject) => subjectObserver.observe(subject));

        mutationObserver.observe(subjects, {
            characterData: true,
            attributes: true,
            childList: true,
            subtree: true
        });

        themeObserver.observe(document.documentElement, {
            attributeFilter: [
                "data-theme"
            ],
            attributes: true
        });

        subjects.addEventListener("transitionend", scheduleMeasure);

        subjects.addEventListener("animationend", scheduleMeasure);

        document.fonts?.ready.then(scheduleMeasure);

        return () => {
            frameObserver.disconnect();

            subjectObserver.disconnect();

            mutationObserver.disconnect();

            themeObserver.disconnect();

            subjects.removeEventListener("transitionend", scheduleMeasure);

            subjects.removeEventListener("animationend", scheduleMeasure);

            if(frameRequestRef.current !== null) {
                window.cancelAnimationFrame(frameRequestRef.current);

                frameRequestRef.current = null;
            }
        };
    }, [
        scheduleMeasure
    ]);

    useLayoutEffect(() => {
        const footer = footerRef.current;

        if(!footer) {
            return;
        }

        const footerObserver = new ResizeObserver(() => {
            setFooterHeight(Math.ceil(footer.offsetHeight));
        });

        footerObserver.observe(footer);

        return () => {
            footerObserver.disconnect();
        };
    }, []);

    const getComponentPoint = (clientX: number, clientY: number) => {
        const stage = stageRef.current;

        if(!stage || !layout) {
            return null;
        }

        const bounds = stage.getBoundingClientRect();

        return {
            x: (clientX - bounds.left - layout.originX) / layout.scale,
            y: (clientY - bounds.top - layout.originY) / layout.scale
        };
    };

    const isInsideObject = (point: AnatomyPointer) => measurement !== null && point.x >= 0 && point.y >= 0 && point.x <= measurement.width && point.y <= measurement.height;

    const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const point = getComponentPoint(event.clientX, event.clientY);

        if(!point || !measurement) {
            return;
        }

        setPointer(point);

        const hit = measurement.parts.filter((part) => point.x >= part.rect.x && point.x <= part.rect.x + part.rect.width && point.y >= part.rect.y && point.y <= part.rect.y + part.rect.height).sort((first, second) => first.rect.width * first.rect.height - second.rect.width * second.rect.height || second.id - first.id)[0];

        setHoveredPartId(hit ? hit.id : null);
    };

    const handlePointerLeave = () => {
        setPointer(null);

        setHoveredPartId(null);
    };

    const handleStageClick = (event: MouseEvent<HTMLDivElement>) => {
        const point = getComponentPoint(event.clientX, event.clientY);

        if(hasStates && point && isInsideObject(point)) {
            setActiveState((current) => (current + 1) % stateOptions.length);
        }
    };

    const activeSegment = useMemo(() => {
        if(!layout || activeSegmentKey === null) {
            return null;
        }

        for(const chain of layout.chains) {
            const segment = chain.segments.find((item) => item.key === activeSegmentKey);

            if(segment) {
                return {
                    isHorizontal: chain.side === "top" || chain.side === "bottom",
                    partIds: segment.partIds,
                    start: segment.start,
                    end: segment.end
                };
            }
        }

        return null;
    }, [
        activeSegmentKey,
        layout
    ]);

    const focusPartId = hoveredPartId ?? (typeof activeCallout === "number" ? activeCallout : pinnedPartId);

    const focusPart = measurement && focusPartId !== null ? measurement.parts[focusPartId] ?? null : null;

    const activePartIds = [
        ...focusPartId === null ? [] : [focusPartId],
        ...activeSegment ? activeSegment.partIds : []
    ];

    const stageWidth = layout?.stageWidth ?? availableWidth;

    const stageHeight = (layout?.stageHeight ?? DEFAULT_MAX_HEIGHT) + footerHeight;

    const scale = layout?.scale ?? 1;

    const scaleBarLength = getScaleBarLength(scale);

    const scaleBarWidth = scaleBarLength * scale;

    return <figure
        style={{
            margin: `${style.spaces.spacingLg}px 0 ${style.spaces.spacingSm}px`
        }}
        aria-label={t("anatomy.figureLabel", [
            name
        ])}
    >
        <DemoFrame>
            <div
                style={SCROLL_STYLE}
                ref={frameRef}
            >
                <div
                    style={{
                        ...STAGE_STYLE,
                        cursor: hasStates ? "pointer" : undefined,
                        height: stageHeight,
                        width: stageWidth
                    }}
                    onPointerLeave={handlePointerLeave}
                    onPointerMove={handlePointerMove}
                    onClick={handleStageClick}
                    ref={stageRef}
                >
                    <div
                        style={SUBJECTS_STYLE}
                        aria-hidden={true}
                        ref={subjectsRef}
                        inert={true}
                    >
                        <div
                            style={{
                                ...SUBJECT_STYLE,
                                width: width ?? "max-content"
                            }}
                            ref={activeSubjectRef}
                        >
                            {stateOptions[activeState]?.node}
                        </div>
                        {
                        hasStates ? stateOptions.map((state, index) => <div
                            ref={(element) => {
                                stateSubjectRefs.current[index] = element;
                            }}
                            style={{
                                ...SUBJECT_STYLE,
                                width: width ?? "max-content"
                            }}
                            key={index}
                        >
                            {state.node}
                        </div>) : null
                        }
                    </div>
                    {
                        layout ?
                            <AnatomyGrid
                                height={stageHeight}
                                width={stageWidth}
                                layout={layout}
                                id={id}
                            />
                        :
                            null
                    }
                    {
                        layout && measurement ?
                            <svg
                                viewBox={`0 0 ${stageWidth} ${stageHeight}`}
                                style={OVERLAY_STYLE}
                                height={stageHeight}
                                aria-hidden={true}
                                width={stageWidth}
                            >
                                {
                                    isHitAreaVisible ?
                                        <AnatomyHatch
                                            isActive={activeCallout === null}
                                            measurement={measurement}
                                            layout={layout}
                                            id={id}
                                        />
                                    :
                                        null
                                }
                                <AnatomyObject
                                    stateLabels={stateOptions.map((state) => upper(state.label))}
                                    activePartIds={activePartIds}
                                    measurement={measurement}
                                    activeState={activeState}
                                    layout={layout}
                                    ghosts={ghosts}
                                />
                                {
                                    layout.chains.map((chain, index) => <AnatomyChainView
                                        onActiveSegmentChange={setActiveSegmentKey}
                                        activeSegmentKey={activeSegmentKey}
                                        key={`${chain.side}-${index}`}
                                        activePartId={focusPartId}
                                        chain={chain}
                                    />)
                                }
                                <AnatomyBalloons
                                    onActiveBalloonChange={setActiveCallout}
                                    activeBalloon={activeCallout}
                                    balloons={layout.balloons}
                                />
                                {
                                    focusPart ?
                                        <AnatomyFocus
                                            measurement={measurement}
                                            part={focusPart}
                                            layout={layout}
                                        />
                                    :
                                        null
                                }
                                <AnatomyRulers
                                    activeSegment={activeSegment}
                                    height={stageHeight}
                                    width={stageWidth}
                                    pointer={pointer}
                                    layout={layout}
                                />
                            </svg>
                        :
                            null
                    }
                    <div
                        style={{
                            ...FOOTER_STYLE,
                            padding: `0 ${style.spaces.spacingLg}px ${style.spaces.spacingMd + style.spaces.spacingXs}px ${style.spaces.spacingXxl - style.spaces.spacingXs}px`,
                            gap: style.spaces.spacingMd,
                            width: availableWidth
                        }}
                        ref={footerRef}
                    >
                        <div
                            style={{
                                ...FOOTER_START_STYLE,
                                gap: style.spaces.spacingMd - style.spaces.spacingXxs
                            }}
                        >
                            {
                                notes && notes.length > 0 ?
                                    <View
                                        role="list"
                                    >
                                        {
                                            notes.map((note) => <View
                                                role="listitem"
                                                key={note}
                                            >
                                                <Text
                                                    variant="bodySmallSize"
                                                    color="low"
                                                >
                                                    {note}
                                                </Text>
                                            </View>)
                                        }
                                    </View>
                                :
                                    null
                            }
                            <svg
                                width={scaleBarWidth + 48}
                                height={SCALE_BAR_HEIGHT}
                                style={{
                                    overflow: "visible"
                                }}
                                aria-hidden={true}
                            >
                                <line
                                    strokeWidth={style.width.thin}
                                    stroke={style.color.line}
                                    x2={scaleBarWidth + 1}
                                    y1={8}
                                    y2={8}
                                    x1={1}
                                />
                                {
                                    [
                                        0,
                                        0.5,
                                        1
                                    ].map((ratio) => <g
                                        key={ratio}
                                    >
                                        <line
                                            strokeWidth={style.width.thin}
                                            x1={1 + ratio * scaleBarWidth}
                                            x2={1 + ratio * scaleBarWidth}
                                            y2={ratio === 0.5 ? 10 : 12}
                                            stroke={style.color.line}
                                            y1={4}
                                        />
                                        <text
                                            textAnchor={ratio === 0 ? "start" : "middle"}
                                            fontFamily={style.text.small.fontFamily}
                                            fontWeight={style.text.small.fontWeight}
                                            fontSize={style.text.small.fontSize}
                                            x={1 + ratio * scaleBarWidth}
                                            fill={style.color.line}
                                            y={25}
                                        >
                                            {ratio === 1 ? `${scaleBarLength}PX` : formatValue(scaleBarLength * ratio)}
                                        </text>
                                    </g>)
                                }
                            </svg>
                        </div>
                        <Card
                            style={{
                                minWidth: style.shapeConfigs.fieldMinWidth
                            }}
                            variant="outline"
                            size="small"
                        >
                            <Card.Header
                                isDivided={true}
                            >
                                <Text
                                    variant="labelSmallSize"
                                    color="mid"
                                >
                                    {name.toLocaleUpperCase("en-US")}
                                </Text>
                            </Card.Header>
                            <Card.Content
                                style={{
                                    rowGap: style.spaces.spacingXs
                                }}
                            >
                                <Text
                                    variant="bodySmallSize"
                                    color="low"
                                >
                                    {upper(`${t("anatomy.scale")} ${scale}:1 · ${t("anatomy.grid")} ${layout?.gridStep ?? 4}PX`)}
                                </Text>
                                {
                                    hasStates ?
                                        <ToggleGroup
                                            onValueChange={(value) => {
                                                const index = stateOptions.findIndex((state) => state.label === value);

                                                if(index >= 0) {
                                                    setActiveState(index);
                                                }
                                            }}
                                            value={stateOptions[activeState]?.label}
                                            title={t("anatomy.states")}
                                            attachBehaviour="attached"
                                            isClearEnabled={false}
                                            variant="outline"
                                            size="small"
                                        >
                                            {
                                                stateOptions.map((state) => <ToggleGroup.Item
                                                    title={upper(state.label)}
                                                    value={state.label}
                                                    key={state.label}
                                                />)
                                            }
                                        </ToggleGroup>
                                    :
                                        null
                                }
                                <Text
                                    variant="bodySmallSize"
                                    aria-hidden={true}
                                    color="low"
                                >
                                    {`${measurement ? `${formatValue(measurement.width)} × ${formatValue(measurement.height)}PX` : ""} · X ${pointer ? formatValue(pointer.x) : "–"}  Y ${pointer ? formatValue(pointer.y) : "–"}`}
                                </Text>
                            </Card.Content>
                        </Card>
                    </div>
                </div>
            </div>
        </DemoFrame>
        {
            measurement && layout ?
                <AnatomyLegend
                    onHitAreaActiveChange={(isActive) => setActiveCallout(isActive ? null : undefined)}
                    isHitAreaActive={activeCallout === null}
                    onActivePartChange={setHoveredPartId}
                    onPinnedPartChange={setPinnedPartId}
                    pinnedPartId={pinnedPartId}
                    activePartId={focusPartId}
                    measurement={measurement}
                    parts={listedParts}
                    hitArea={hitArea}
                    names={names}
                    t={t}
                />
            :
                null
        }
        {
            caption ?
                <figcaption>
                    <Caption>
                        {caption}
                    </Caption>
                </figcaption>
            :
                null
        }
    </figure>;
};
export default Anatomy;
